import { AddProductSchema } from '../components/AddProductContent';
import { SellerProductFullDetails } from '@/components/Products/ProductDetails/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CategoryAttributeDetail {
  id: string;
  nameEn: string;
  nameAr: string;
  name: string;
}

export interface CategoryAttribute {
  id: string;
  nameEn: string;
  nameAr: string;
  name: string;
  isColor: boolean;
  categoryAttributeDetails: CategoryAttributeDetail[];
}

// Shape of a single variant attribute from the API
type VariantAttribute = NonNullable<
  NonNullable<SellerProductFullDetails['variants']>[number]['attributes']
>[number];

// Shape the form expects for a single color entry
type MappedColor = {
  color: { id: string | null; name: string };
  image: { uid: string; name: string; url?: string; uploading?: boolean; status?: string }[];
};

/**
 * Shared dependencies passed to variant-processing functions.
 * Grouping them avoids repeating the same two parameters across every helper signature.
 * Add new shared lookups here as the mapper grows.
 */
interface VariantMappingContext {
  colorAttribute: CategoryAttribute | undefined;
  attributesById: Map<string, CategoryAttribute>;
}

// ─── Context Builder ──────────────────────────────────────────────────────────

/**
 * Builds the lookup context used by all variant-processing helpers.
 * Creating the Map once here gives every downstream function O(1) attribute access
 * instead of repeatedly scanning the array.
 */
function buildMappingContext(categoryAttributes: CategoryAttribute[]): VariantMappingContext {
  return {
    colorAttribute: categoryAttributes.find((a) => a.isColor),
    attributesById: new Map(categoryAttributes.map((a) => [a.id, a])),
  };
}

// ─── Attribute Lookup Helpers ─────────────────────────────────────────────────

/**
 * Returns the display name for a specific attribute detail.
 * Falls back to 'Unknown' if the attribute or detail cannot be found.
 */
function findDetailName(
  attributesById: Map<string, CategoryAttribute>,
  attrId: string,
  detailId: string
): string {
  const attr = attributesById.get(attrId);
  const detail = attr?.categoryAttributeDetails.find((d) => d.id === detailId);
  return detail?.name ?? '';
}

/**
 * Resolves the display name for a variant attribute.
 *
 * Priority:
 *  1. Standard detail name (looked up by detailId)
 *  2. nameEn from the API response
 *  3. nameAr from the API response
 *  4. 'Custom Color' as a last resort (for fully custom, un-catalogued colors)
 */
function getAttributeDisplayName(
  attr: any,
  attributesById: Map<string, CategoryAttribute>
): string {
  if (attr.categoryAttributeDetailId) {
    const detailName = findDetailName(
      attributesById,
      attr.categoryAttributeId,
      attr.categoryAttributeDetailId
    );
    if (detailName) return detailName;
  }
  return attr.categoryAttributeDetailName || attr.nameEn || attr.nameAr || '';
}

// ─── Section Mappers ──────────────────────────────────────────────────────────

/**
 * Sorts product images thumbnail-first (then by sortOrder) and maps them to
 * the uploader's stub format. No File object is attached since these are
 * already-uploaded images being restored in edit mode.
 */
function mapProductImages(
  images: any[]
): AddProductSchema['productImages'] {
  // TODO: file is optional on UploadedImage in edit mode — update the type definition
  //       to make file?: File, then this cast can be removed
  return [...(images ?? [])]
    .sort((a, b) => {
      const aThumb = a.isThumbnail || a.isDefault;
      const bThumb = b.isThumbnail || b.isDefault;
      if (aThumb) return -1;
      if (bThumb) return 1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    })
    .map((img) => ({ id: img.id, preview: img.url, uploading: false })) as unknown as AddProductSchema['productImages'];
}

/**
 * Maps API tier fields to the form values.
 * Note: Form fields now map 1:1 with API names.
 */
function mapPricingTiers(tiers: SellerProductFullDetails['pricingTiers']) {
  return (tiers ?? []).map((tier) => ({
    minQty: tier.minQty,
    maxQty: tier.maxQty,
    pricePerUnit: tier.pricePerUnitConverted,
    internalLabel: tier.internalLabel ?? '',
  }));
}

/**
 * Returns a single-item array with the logo stub when a URL is available,
 * or an empty array when there's no logo to restore.
 */
function mapLogo(
  logoImageUrl?: string | null,
  logoImageId?: string | null,
  logoName?: string | null,
  logoBlobSize?: number | null
) {
  if (!logoImageUrl) return [];
  return [{
    uid: logoImageId ?? logoImageUrl,
    name: logoName ?? 'logo',
    url: logoImageUrl,
    status: 'done',
    size: logoBlobSize ?? undefined,
    uploading: false,
  }];
}

// ─── Variant Processors ───────────────────────────────────────────────────────

/**
 * Iterates all variant attributes and builds:
 *  - variantColors:      one entry per unique color (drives the color picker UI)
 *  - mappedAttributes:   attrId → [detailId, ...] (drives the attribute selector UI)
 *
 * Custom colors (no detailId) are deduped by name to avoid duplicate entries.
 */
function buildVariantMetadata(
  variants: SellerProductFullDetails['variants'],
  context: VariantMappingContext,
  rootAttributes: any[] = []
): { variantColors: MappedColor[]; mappedAttributes: Record<string, string[]> } {
  const { colorAttribute, attributesById } = context;

  const variantColors: MappedColor[] = [];
  const mappedAttributes: Record<string, Set<string>> = {};
  const seenColorKeys = new Set<string>();

  (variants ?? []).forEach((variant) => {
    (variant.attributes ?? []).forEach((attr) => {
      const { categoryAttributeId, categoryAttributeDetailId, colorImageId, colorImageUrl } = attr;
      const isColorAttr = colorAttribute?.id === categoryAttributeId;

      // Skip attributes that carry no meaningful value
      if (!categoryAttributeDetailId && !isColorAttr) return;

      // ── Non-color attributes: collect unique detail IDs per attribute ────
      if (categoryAttributeDetailId) {
        if (!mappedAttributes[categoryAttributeId]) {
          mappedAttributes[categoryAttributeId] = new Set();
        }
        mappedAttributes[categoryAttributeId].add(categoryAttributeDetailId);
      }

      // ── Color attributes: collect one entry per unique color ─────────────
      if (isColorAttr) {
        const name = getAttributeDisplayName(attr, attributesById);
        // Custom colors have no detailId — key by name to prevent duplicates
        const dedupeKey = categoryAttributeDetailId ?? `custom-${name}`;

        if (!seenColorKeys.has(dedupeKey)) {
          seenColorKeys.add(dedupeKey);
          variantColors.push({
            color: { id: categoryAttributeDetailId, name },
            image: colorImageId
              ? [{ uid: colorImageId, name: 'color', url: colorImageUrl ?? '', uploading: false }]
              : [],
          });
        }
      }
    });
  });

  // ── Parse root attributes (useful for products without explicit variants) ──
  (rootAttributes ?? []).forEach((attrObj) => {
    const attrName = attrObj.attribute?.name;
    if (!attrName) return;

    // Find the matching CategoryAttribute by name
    const categoryAttr = Array.from(attributesById.values()).find(
      (ca) => ca.nameEn === attrName || ca.name === attrName || ca.nameAr === attrName
    );
    if (!categoryAttr) return;

    const isColorAttr = colorAttribute?.id === categoryAttr.id;

    (attrObj.attribute?.values || []).forEach((val: any) => {
      const valName = val.name;
      if (!valName) return;

      const detail = categoryAttr.categoryAttributeDetails.find(
        (d) => d.nameEn === valName || d.name === valName || d.nameAr === valName
      );
      const detailId = detail?.id;

      if (isColorAttr) {
        const dedupeKey = detailId ?? `custom-${valName}`;
        if (!seenColorKeys.has(dedupeKey)) {
          seenColorKeys.add(dedupeKey);
          variantColors.push({
            color: { id: detailId || null, name: valName },
            image: val.colorImageId
              ? [{ uid: val.colorImageId, name: 'color', url: val.url ?? '', uploading: false }]
              : [],
          });
        }
      } else {
        if (detailId) {
          if (!mappedAttributes[categoryAttr.id]) {
            mappedAttributes[categoryAttr.id] = new Set();
          }
          mappedAttributes[categoryAttr.id].add(detailId);
        }
      }
    });
  });

  const finalMappedAttributes: Record<string, string[]> = {};
  for (const [key, set] of Object.entries(mappedAttributes)) {
    finalMappedAttributes[key] = Array.from(set);
  }

  return { variantColors, mappedAttributes: finalMappedAttributes };
}

/**
 * Maps each API variant to the form's variant shape.
 * Builds _displayDetails (for rendering attribute badges) and
 * _permutationKey (for matching generated permutations to existing variants).
 */
function mapVariants(
  variants: SellerProductFullDetails['variants'],
  context: VariantMappingContext,
  isDuplicate: boolean = false,
  exchangeRate: number = 3.75
) {
  const { colorAttribute, attributesById } = context;

  return (variants ?? []).map((variant) => {
    const validAttributes = (variant.attributes ?? []).filter(
      (attr) => attr.categoryAttributeDetailId || colorAttribute?.id === attr.categoryAttributeId
    );

    const _displayDetails = validAttributes.map((attr) => {
      const isColorAttr = colorAttribute?.id === attr.categoryAttributeId;
      const name = getAttributeDisplayName(attr, attributesById);

      return {
        attrId: isColorAttr ? 'color' : attr.categoryAttributeId,
        detail: { id: attr.categoryAttributeDetailId, name, nameEn: name, nameAr: name },
      };
    });

    const variantOptionNames = validAttributes.map((attr) =>
      getAttributeDisplayName(attr, attributesById)
    );

    return {
      sku: isDuplicate ? null : variant.sku,
      stock: variant.stock,
      priceConverted: variant.priceConverted ?? undefined,
      // Prefer the stored price from the API; fall back to back-calculating from priceConverted
      price: variant.price ?? (variant.priceConverted ? variant.priceConverted / exchangeRate : undefined),
      _displayDetails,
      // Sorted so permutation key is order-independent: "Red-Small" === "Small-Red"
      _permutationKey: variantOptionNames.sort().join('-'),
    };
  });
}

// ─── Main Mapper ──────────────────────────────────────────────────────────────

/**
 * Maps the API response from GET_SELLER_PRODUCT_FULL_DETAILS
 * into the shape expected by the Add Product form (AddProductSchema).
 *
 * Used exclusively in edit/update mode when a productId is present.
 */
export const mapProductToFormValues = (
  data: SellerProductFullDetails,
  categoryAttributes: CategoryAttribute[] = [],
  isDuplicate: boolean = false,
  exchangeRate: number = 3.75
): Partial<AddProductSchema> => {
  const context = buildMappingContext(categoryAttributes);
  const variants = data.variants ?? [];

  const { variantColors, mappedAttributes } = buildVariantMetadata(variants, context, (data as any).attributes);
  const mappedVariants = mapVariants(variants, context, isDuplicate, exchangeRate);

  const productInfo = {
    productName: data.productName ?? '',
    description: data.description ?? '',
    categoryId: data.categoryId ?? (data as any).category?.id ?? '',
    specialityId: data.specialityId ?? (data as any).specialty?.id ?? undefined,
    hsCodeId: data.hsCodeId ?? (data as any).hsCode?.id ?? undefined,
    productImages: mapProductImages(data.productImages ?? (data as any).images),
  };

  // ─── Section 2: Pricing & Trade ────────────────────────────────────────────
  const tradeInfo = {
    minQty: data.minQty ?? undefined,
    leadTime: data.leadTime ?? undefined,
  };

  // ─── Section 3: Specifications ──────────────────────────────────────────────
  const specifications = {
    specs: (data.specs ?? []).map((s) => ({ type: s.name, value: s.value })),
    itemPerCarton: data.itemPerCarton ?? undefined,
    grossWeight: data.grossWeight ?? undefined,
    height: data.height ?? undefined,
    width: data.width ?? undefined,
    length: data.length ?? undefined,
  };

  // ─── Section 4: SKU & Inventory ────────────────────────────────────────────
  const inventory = {
    // 'RFQ_METHOD' → 'rfq'; everything else (incl. 'CART' and null) → 'cart'
    productType: (data.priceType === 'RFQ_METHOD' ? 'rfq' : 'cart') as 'rfq' | 'cart',
    applyVariants: data.applyVariants || variants.length > 0,
    applyPricingTiers: data.applyPricingTiers ?? false,
    basePrice: data.productPriceConverted ?? undefined,
    baseStock: data.stock ?? undefined,
    pricingTiers: mapPricingTiers(data.pricingTiers),
    colors: variantColors.length > 0 ? variantColors : undefined,
    colorAttributeId: context.colorAttribute?.id,
    attributes: Object.keys(mappedAttributes).length > 0 ? mappedAttributes : undefined,
    hasVariants: variants.length > 0,
    variants: mappedVariants,
    locationId: data.locationId ?? undefined,
  };

  // ─── Section 5: Customization & OEM ────────────────────────────────────────
  const customization = {
    acceptSampleOrder: data.acceptSampleOrder ?? false,
    minSampleQty: data.minSampleQty ?? undefined,
    samplePrice: data.samplePrice ?? undefined,
    customLogo: mapLogo(data.logoImageUrl, data.logoImageId, data.logoName, data.logoBlobSize),
    certificatesDocuments: (data.certificateDocs ?? []).map((doc: any) => ({
      uid: doc.id,
      name: doc.url ? doc.url.split('/').pop() || doc.name : doc.name,
      certificateName: doc.name,
      url: doc.url || '',
      status: 'done',
      type: doc.extension ? doc.extension.replace('.', '') : undefined,
    })),
    customizationTypes: data.customizationTypes ?? [],
  };

  return {
    ...productInfo,
    ...tradeInfo,
    ...specifications,
    ...inventory,
    ...customization,
  };
};
